import { useBloc } from "@blac/react";
import { Cubit } from "blac-next";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NotificationsCubit from "src/state/NotificationsCubit/NotificationsCubit";
import ConversationListCubit from "../LeapChat/state/ConversationListCubit";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { tracker } from "src/state/state";

interface AppNotificationsRedirectState {
  redirectTo: URL | null;
}

export class AppNotificationsRedirectCubit extends Cubit<AppNotificationsRedirectState> {
  constructor() {
    super({
      redirectTo: null
    });
  }

  // #region Section Storage for IDs which we already seen/checked
  rememberedRedirectItemsKey = "appNotificationsRedirectSeenItems";
  limitDaysToRemember = 30;
  dateIdSeparator = ":";

  // parse `<YYYY-MM-DD>:<id>` format, return id if date is today, otherwise return null
  parseIdFromStorage = (storedValue: string): string | null => {
    const [, id] = storedValue.split(this.dateIdSeparator);
    return id;
  };

  // add date prefix to id and store in format `<YYYY-MM-DD>:<id>`
  prepareForStorage = (id: string): string => {
    // eslint-disable-next-line @typescript-eslint/prefer-destructuring
    const today = new Date().toISOString().split("T")[0];
    return `${today}${this.dateIdSeparator}${id}`;
  };

  // removes items older than limitDaysToRemember from the stored list
  filterOutOldRemembered = (items: string[]) => {
    const today = new Date();
    return items.filter((item) => {
      const [dateStr] = item.split(this.dateIdSeparator);
      const itemDate = new Date(dateStr);
      const diffInDays =
        (today.getTime() - itemDate.getTime()) / (1000 * 3600 * 24);
      return diffInDays <= this.limitDaysToRemember;
    });
  };

  getRememberedRedirectItems = () => {
    try {
      const itemsRaw =
        StorageController.getItem(this.rememberedRedirectItemsKey) ?? "[]";
      const items = JSON.parse(itemsRaw) as string[];
      return this.filterOutOldRemembered(items);
    } catch (e) {
      reportErrorSentry(
        new Error("Failed to parse remembered redirect items"),
        {
          extra: {
            error: e instanceof Error ? e.message : String(e),
            itemsRaw: StorageController.getItem(this.rememberedRedirectItemsKey)
          }
        }
      );
      return [];
    }
  };

  rememberSeenUnread = (items: { id: string }[] = []) => {
    const currentRemembered = this.getRememberedRedirectItems();
    const newIds = items.map((item) => this.prepareForStorage(item.id));
    const updatedRemembered = Array.from(
      new Set([...currentRemembered, ...newIds])
    );
    StorageController.setItem(
      this.rememberedRedirectItemsKey,
      JSON.stringify(updatedRemembered)
    );
  };

  filterOutRememberedItems = <T extends { id: string }>(items: T[]) => {
    const rememberedIds = this.getRememberedRedirectItems();
    return items.filter(
      (item) =>
        !rememberedIds.find(
          (remembered) => this.parseIdFromStorage(remembered) === item.id
        )
    );
  };
  // #endregion

  // #region Section Redirect Logic
  checkForRedirect = (options: {
    unreadConversations: typeof ConversationListCubit.prototype.state.conversations;
    unreadNotifications: typeof NotificationsCubit.prototype.state.notifications;
    location?: Location;
  }) => {
    const {
      unreadConversations,
      unreadNotifications,
      location = window.location
    } = options;
    const redirectOnPaths = ["/app/home", "/app/chat"];
    const canRedirect = redirectOnPaths.some((path) =>
      location.pathname.startsWith(path)
    );

    const newConversations = this.filterOutRememberedItems(unreadConversations);
    const newNotifications = this.filterOutRememberedItems(unreadNotifications);

    this.rememberSeenUnread([...newConversations, ...newNotifications]);

    const deps = [newConversations, newNotifications] as const;
    const hasNew = deps.some((arr) => arr.length > 0);
    const hasOnlyOneType = !deps.every((arr) => arr.length > 0);

    if (canRedirect && hasNew && hasOnlyOneType) {
      this.determineRedirect({
        unreadConversations: newConversations,
        unreadNotifications: newNotifications,
        location
      });
    }
  };

  determineRedirect = (options: {
    unreadConversations: typeof ConversationListCubit.prototype.state.conversations;
    unreadNotifications: typeof NotificationsCubit.prototype.state.notifications;
    location?: Location;
  }) => {
    const {
      unreadConversations,
      unreadNotifications,
      location = window.location
    } = options;

    const hasUnreadConversations = unreadConversations.length > 0;
    const hasUnreadNotifications = unreadNotifications.length > 0;

    const currentPath = location.pathname;
    let goTo = new URL(location.href);

    if (hasUnreadConversations && !hasUnreadNotifications) {
      goTo = this.patchPathname("/app/chat");
    } else if (!hasUnreadConversations && hasUnreadNotifications) {
      goTo = this.patchPathname("/app/home");
    }

    const goToIsCurrentPath = goTo.pathname === currentPath;
    if (goToIsCurrentPath) {
      return;
    }

    tracker.track("Redirecting user based on unread notifications", {
      data: {
        hasUnreadConversations,
        hasUnreadNotifications,
        currentPath,
        goTo: goTo.pathname
      }
    });

    this.patch({
      redirectTo: goTo
    });
  };

  patchPathname = (path: string) => {
    const { location } = window;
    const url = new URL(location.href);
    url.pathname = path;
    return url;
  };
  // #endregion
}

const AppNotificationsRedirect = () => {
  const [conversationState] = useBloc(ConversationListCubit);
  const [notificationState] = useBloc(NotificationsCubit);
  const [{ redirectTo }, { checkForRedirect }] = useBloc(
    AppNotificationsRedirectCubit
  );

  const navigate = useNavigate();

  useEffect(() => {
    if (
      conversationState.initialLoadComplete &&
      notificationState.initialLoadComplete
    ) {
      checkForRedirect({
        unreadConversations: conversationState.conversations.filter(
          (c) => c.unreadMessageCount > 0
        ),
        unreadNotifications: notificationState.notifications
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkForRedirect,
    conversationState.initialLoadComplete,
    notificationState.initialLoadComplete
  ]);

  useEffect(() => {
    if (redirectTo) {
      navigate(`${redirectTo.pathname}${redirectTo.search}`, { replace: true });
    }
  }, [redirectTo, navigate]);

  return null;
};

export default AppNotificationsRedirect;
